import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    forwardRef,
    inject,
    Input,
    OnInit
} from '@angular/core';
import { ControlValueAccessor, FormBuilder, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Permission, Role } from 'app/enums';
import { SelectRolesForm } from './select-roles.model';
import { CachedValuesService, PermissionService } from '@services/index';
import { SubscriptionResponse } from '@services/api-client/responses';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-select-roles',
    templateUrl: './select-roles.component.html',
    styleUrl: './select-roles.component.scss',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SelectRolesComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectRolesComponent implements ControlValueAccessor, OnInit {
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly permissionService = inject(PermissionService);
    private readonly cachedValuesService = inject(CachedValuesService);
    private readonly translateService = inject(TranslateService);

    @Input() public disabled: boolean;

    public selectRolesForm: FormGroup<SelectRolesForm>;
    private subscription: SubscriptionResponse;

    private hasAdminRole = this.permissionService.hasAnyPermission([Permission.WriteUser]);

    public get f() {
        return this.selectRolesForm.controls;
    }

    public ngOnInit() {
        const reader = this.value.includes(Role.Reader);
        const user = this.value.includes(Role.User);
        const fb = new FormBuilder();
        this.selectRolesForm = fb.nonNullable.group({
            [Role.Admin]: this.value.includes(Role.Admin),
            [Role.Reader]: reader,
            [Role.User]: user
        });

        this.cachedValuesService.subscription$.subscribe((response) => {
            this.subscription = response;
            if (!this.subscription.canCreateUserRole || !this.hasAdminRole) {
                this.selectRolesForm.controls[Role.Admin].disable();
                this.selectRolesForm.controls[Role.User].disable();
            }
            if (!this.subscription.canCreateReaderRole || !this.hasAdminRole)
                this.selectRolesForm.controls[Role.Reader].disable();
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    public onTouched = () => {};
    public onChange = (value: Role[]): Role[] => value;
    public roles = [Role.Admin, Role.Reader, Role.User];
    public value: Role[] = [];

    public getRoleText(role: Role): string {
        let roleName = this.translateService.instant('ROLES.' + role) as string;
        if (role === Role.Reader)
            roleName =
                roleName +
                ' (x/y)'
                    .replace('x', this.subscription.activeReaders.toString())
                    .replace('y', this.subscription.numOfReaders.toString());
        if (role === Role.User)
            roleName =
                roleName +
                ' (x/y)'
                    .replace('x', this.subscription.activeUsers.toString())
                    .replace('y', this.subscription.numOfUsers.toString());

        return roleName;
    }
    public writeValue(value: Role[]) {
        if (!this.disabled) {
            this.value = value;
            this.roles.forEach((role) => {
                const checked = value.includes(role);
                if (checked) this.f[role].enable();
                if (role === Role.Reader && checked) {
                    this.selectRolesForm.controls[Role.Admin].disable();
                }
                this.f[role].setValue(checked);
            });
            this.changeDetectorRef.markForCheck();
        }
    }

    public registerOnChange(fn: (value: Role[]) => Role[]) {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void) {
        this.onTouched = fn;
    }

    public setDisabledState?(disabled: boolean) {
        this.disabled = disabled;
        if (disabled) this.selectRolesForm.disable();
        this.changeDetectorRef.markForCheck();
    }

    public roleChange(role: Role, checked: boolean) {
        if (checked) {
            if (role === Role.Reader) {
                this.f[Role.User].setValue(false);
                this.f[Role.Admin].setValue(false);
                this.selectRolesForm.controls[Role.Admin].disable();
            }
            if (role === Role.User) {
                this.f[Role.Reader].setValue(false);
                this.selectRolesForm.controls[Role.Admin].enable();
            }
        }
        const roles = this.roles.filter((r) => (r === role ? checked : this.value.includes(r)));
        this.value = roles;
        this.onChange(roles);
    }
}
